'use client'
import { Box, Flex, ScrollArea, Text } from '@mantine/core'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { useMemo, useState } from 'react'

import BasicLineChart from '@/app/[locale]/price/[slug]/_components/CoinLineChart/components/BasicLineChart'
import TimeRangeItem from '@/app/[locale]/price/[slug]/_components/CoinLineChart/components/TimeRangeItem'
import TradingViewStockChart from '@/app/[locale]/price/stocks/[symbol]/_components/TradingViewStockChart'
import { useStockContext } from '@/app/[locale]/price/stocks/[symbol]/_contexts/StockContext'
import InfoCircle from '@/assets/icons/info-circle.svg'
import { SectionErrorBoundary } from '@/components/error/boundary'
import { useSsrContext } from '@/contexts/ssr'
import { useStockPricesById } from '@/services/stock/prices'

import ChartInfoTitle from './components/ChartInfoTitle'
import StockTitle from './components/StockTitle'
import { useTitle } from './hooks'
import placeholderChart from './placeholder_price_chart.webp'
import { CHART_HEIGHT, SettingType, TIME_RANGE_OPTIONS } from './utils'

interface StockLineChartProps {
  customHeader?: React.ReactNode
}

function StockLineChart({ customHeader }: StockLineChartProps) {
  const { stock } = useStockContext()
  const t = useTranslations('chart')
  const { slug, id } = stock
  const [curTimeRange, setCurTimeRange] = useState('24H')
  const [settingType, _] = useState<SettingType | null>(null)
  const queryParams = useMemo(() => {
    const targetOption = TIME_RANGE_OPTIONS.find(option => option.value === curTimeRange)
    return targetOption!.getQueryParams()
  }, [curTimeRange])
  const { selectedCurrency } = useSsrContext()
  const { data: currentStockPrices, isFetching: isFetchingCurrentStockPrices } = useStockPricesById(
    {
      assetId: id,
      ...queryParams,
    },
  )

  const { setMousePointData, stokcPrice, stockPriceChange, isMouseInChart } = useTitle({
    btcPricesVisible: false,
  })
  return (
    <Box flex={1}>
      <StockTitle
        currentStockPrices={currentStockPrices}
        curTimeRange={curTimeRange}
        slug={slug}
        customHeader={customHeader}
      />
      <ChartInfoTitle
        currency={selectedCurrency}
        stokcPrice={stokcPrice}
        stockPriceChange={stockPriceChange}
        isMouseInChart={isMouseInChart}
      />
      {settingType === SettingType.TRADING_VIEW ? (
        <TradingViewStockChart />
      ) : isFetchingCurrentStockPrices ? (
        <Box
          h={CHART_HEIGHT}
          w={'100%'}
          pos="relative"
          style={{ borderRadius: 8, overflow: 'hidden' }}
        >
          <Image
            src={placeholderChart}
            alt="Loading chart"
            fill
            style={{ objectFit: 'contain' }}
            priority
          />
        </Box>
      ) : (
        <BasicLineChart
          key={curTimeRange}
          btcPricesVisible={false}
          currentCoinPrices={currentStockPrices}
          onShowPriceTooltip={setMousePointData}
        />
      )}
      <Flex align={'center'} h={36}>
        {/* <SettingButton
          settingType={settingType}
          onSettingChange={setSettingType}
          bTCPriceButtonVisible={false}
        /> */}
        <ScrollArea>
          <Flex>
            {settingType !== SettingType.TRADING_VIEW &&
              TIME_RANGE_OPTIONS.map(item => (
                <TimeRangeItem
                  key={item.value}
                  isActive={curTimeRange === item.value}
                  value={item.value}
                  label={item.label}
                  onClick={setCurTimeRange}
                />
              ))}
          </Flex>
        </ScrollArea>
      </Flex>
      <Flex
        mt={{ base: 16, md: 40 }}
        gap={8}
        p={12}
        justify={'flex-start'}
        bdrs={24}
        bg={'var(--bg-surface-secondary)'}
      >
        <InfoCircle flex={1} height={20} fill={'var(--common-white)'} />
        <Text c={'var(--content-secondary)'} variant="subtitle">
          {t('disclaimer')}
        </Text>
      </Flex>
    </Box>
  )
}
export default SectionErrorBoundary.with(StockLineChart)
